import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getPublicSolutions } from "../api/solutions";
import { getPublicSectors, getPublicSectorBySlug } from "../api/sectors";
import { getBlogPosts } from "../api/blog";
import { fetchAllDownloadsDocuments } from "../utils/downloadsDocuments";
import {
  mergeSolutionWithEnglishFallback,
  normalizeSolutionLanguage,
} from "../utils/solutionLanguageMerge";
import { queryKeys } from "../query/queryKeys";
import {
  fetchPageContentWithAliases,
  fetchProductsExploreSection,
  normalizePublicLanguage,
  safeGetPageContent,
} from "../query/publicFetchers";

const findSolutionByKey = (solutions, solutionKey) => {
  const key = String(solutionKey || "").trim().toLowerCase();
  if (!key || !Array.isArray(solutions)) return null;
  return (
    solutions.find((item) => String(item?.solutionKey || "").toLowerCase() === key) || null
  );
};

export function usePageContent(page, language, options = {}) {
  const lang = normalizePublicLanguage(language);
  const query = useQuery({
    queryKey: queryKeys.pageContent(page, lang),
    queryFn: () => safeGetPageContent({ page, language: lang }),
    ...options,
  });

  return {
    ...query,
    data: query.data ?? {},
  };
}

export function usePageContentAliases(aliases, language, options = {}) {
  const lang = normalizePublicLanguage(language);
  const query = useQuery({
    queryKey: queryKeys.pageContentAliases(aliases, lang),
    queryFn: () => fetchPageContentWithAliases(aliases, lang),
    ...options,
  });

  return {
    ...query,
    data: query.data ?? {},
  };
}

export function useProductsExploreSection(language, options = {}) {
  const lang = normalizePublicLanguage(language);
  const query = useQuery({
    queryKey: queryKeys.productsExploreSection(lang),
    queryFn: () => fetchProductsExploreSection(lang),
    ...options,
  });

  return {
    ...query,
    data: query.data ?? null,
  };
}

export function usePublicSolutions(language, options = {}) {
  const lang = normalizeSolutionLanguage(language);
  const query = useQuery({
    queryKey: queryKeys.publicSolutions(lang),
    queryFn: async () => {
      try {
        const data = await getPublicSolutions(lang);
        return Array.isArray(data) ? data : [];
      } catch {
        return [];
      }
    },
    ...options,
  });

  return {
    ...query,
    data: query.data ?? [],
  };
}

export function usePublicSolutionByKey(solutionKey, language) {
  const lang = normalizeSolutionLanguage(language);
  const { data: langSolutions = [] } = usePublicSolutions(lang, {
    enabled: Boolean(solutionKey),
  });
  const { data: enSolutions = [] } = usePublicSolutions("en", {
    enabled: Boolean(solutionKey) && lang !== "en",
  });

  const data = useMemo(() => {
    const langData = findSolutionByKey(langSolutions, solutionKey);
    const enData = lang !== "en" ? findSolutionByKey(enSolutions, solutionKey) : null;
    return mergeSolutionWithEnglishFallback(langData, enData) || langData || null;
  }, [langSolutions, enSolutions, solutionKey, lang]);

  return { data };
}

export function usePublicSectors(language, options = {}) {
  const lang = normalizePublicLanguage(language);
  const query = useQuery({
    queryKey: queryKeys.publicSectors(lang),
    queryFn: async () => {
      try {
        const data = await getPublicSectors(lang);
        return Array.isArray(data) ? data : [];
      } catch {
        return [];
      }
    },
    ...options,
  });

  return {
    ...query,
    data: query.data ?? [],
  };
}

export function usePublicSectorBySlug(slug, language, options = {}) {
  const lang = normalizePublicLanguage(language);
  const query = useQuery({
    queryKey: queryKeys.publicSectorBySlug(slug, lang),
    queryFn: async () => {
      try {
        return (await getPublicSectorBySlug(slug, lang)) || null;
      } catch {
        return null;
      }
    },
    enabled: Boolean(slug) && options.enabled !== false,
    ...options,
  });

  return {
    ...query,
    data: query.data ?? null,
  };
}

export function useDownloadsDocuments(language, options = {}) {
  const lang = normalizePublicLanguage(language);
  const query = useQuery({
    queryKey: queryKeys.downloadsDocuments(lang),
    queryFn: async () => {
      try {
        return await fetchAllDownloadsDocuments(lang);
      } catch {
        return [];
      }
    },
    ...options,
  });

  return {
    ...query,
    data: query.data ?? [],
  };
}

export function useBlogPosts(language, options = {}) {
  const lang = normalizePublicLanguage(language);
  const query = useQuery({
    queryKey: queryKeys.blogPosts(lang),
    queryFn: async () => {
      try {
        const data = await getBlogPosts({ language: lang });
        return Array.isArray(data) ? data : [];
      } catch {
        return [];
      }
    },
    ...options,
  });

  return {
    ...query,
    data: query.data ?? [],
  };
}
